import { useState, useEffect, memo } from "react";
import mapboxgl from "mapbox-gl";

interface MarkerProps {
  position?: [number, number]; // [lng, lat]
  onClick?: (event: mapboxgl.MapMouseEvent) => void;
  onDragEnd?: (marker: mapboxgl.Marker) => void;
  icon?: {
    url: string;
    scaledSize?: { width: number; height: number };
  };
  map?: mapboxgl.Map;
  draggable?: boolean;
}

const Marker: React.FC<MarkerProps> = ({ 
  onClick, 
  onDragEnd, 
  position, 
  icon, 
  map, 
  draggable = false,
}) => {
  const [marker, setMarker] = useState<mapboxgl.Marker>();

  useEffect(() => {
    if (!map || !position) return;

    if (!marker) {
      // Create marker element if icon is provided
      let markerElement: HTMLElement | undefined;
      if (icon?.url) {
        markerElement = document.createElement('div');
        markerElement.style.width = icon.scaledSize ? `${icon.scaledSize.width}px` : '32px';
        markerElement.style.height = icon.scaledSize ? `${icon.scaledSize.height}px` : '32px';
        markerElement.style.backgroundImage = `url(${icon.url})`;
        markerElement.style.backgroundSize = 'contain';
        markerElement.style.backgroundRepeat = 'no-repeat';
        markerElement.style.backgroundPosition = 'center';
      }

      const newMarker = new mapboxgl.Marker({
        element: markerElement,
        draggable: draggable || !!onDragEnd,
      })
        .setLngLat(position)
        .addTo(map);

      setMarker(newMarker);
    }

    // remove marker from map on unmount
    return () => {
      if (marker) {
        marker.remove();
      }
    };
  }, [marker, map, position, icon, draggable, onDragEnd]);

  useEffect(() => {
    if (marker && position) {
      marker.setLngLat(position);
    }
  }, [marker, position]);

  useEffect(() => {
    if (marker) {
      if (onClick) {
        marker.getElement().addEventListener("click", onClick as any);
      }
      if (onDragEnd) {
        marker.on("dragend", () => onDragEnd(marker));
      }
    }

    return () => {
      if (marker) {
        if (onClick) {
          marker.getElement().removeEventListener("click", onClick as any);
        }
        if (onDragEnd) {
          marker.off("dragend", () => onDragEnd(marker));
        }
      }
    };
  }, [marker, onClick, onDragEnd]);

  return null;
};

export default memo(Marker);
